import { resizeImage } from '~/shared/lib/resizeImage';
import type { MediaItem } from './types';

// Width of the copy loaded to measure the ratio, also the grid's smallest candidate
export const MEASURE_IMAGE_WIDTH = 240;

function measureItemRatios(
  items: MediaItem[],
  onUpdate: (id: MediaItem['id'], ratio: number) => void,
): () => void {
  if (!import.meta.client) {
    return () => {};
  }

  let cancelled = false;

  items.forEach((item) => {
    if (!item.src) {
      return;
    }

    const image = new Image();

    image.onload = () => {
      if (cancelled) {
        return;
      }
      onUpdate(item.id, image.naturalWidth / image.naturalHeight);
    };

    image.onerror = () => {
      if (cancelled) {
        return;
      }
      onUpdate(item.id, 1);
    };

    // A small copy keeps the ratio without pulling the original
    image.src = resizeImage(item.src, { width: MEASURE_IMAGE_WIDTH });
  });

  return () => {
    cancelled = true;
  };
}

export function useImageRatios(items: Readonly<Ref<MediaItem[]>>) {
  const ratioMap = ref(new Map<MediaItem['id'], number>());

  watch(
    items,
    (list, _, onCleanup) => {
      ratioMap.value = new Map();
      const cancel = measureItemRatios(list, (id, ratio) => {
        ratioMap.value.set(id, ratio);
      });
      onCleanup(cancel);
    },
    { immediate: true },
  );

  return {
    ratioMap,
  };
}
